import { useEffect, useState, useCallback, useId } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../data/apiFetch";
import { createTrialRequest } from "../data/requests";
import { fetchTutorReviews } from "../data/reviews";
import { toId } from "../lib/subjects";
import { auth as fbAuth } from "../firebase";
import { credentialFlags } from "../lib/academic";
import { formatPackageMeta } from "../lib/packageTemplates";

/* ----------------- Stars ----------------- */
function Star({ filled = 0, gradientId }) {
  if (filled === 1) {
    return (
      <svg
        viewBox="0 0 24 24"
        className="h-3.5 w-3.5 text-[#f7cf52]"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.402 8.168L12 18.902l-7.336 3.864 1.402-8.168L.132 9.211l8.2-1.193L12 .587z" />
      </svg>
    );
  }

  if (filled === 0.5) {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[#f7cf52]" aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="100%" y2="0">
            <stop offset="50%" stopColor="#f7cf52" />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <path
          d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.402 8.168L12 18.902l-7.336 3.864 1.402-8.168L.132 9.211l8.2-1.193L12 .587z"
          fill={`url(#${gradientId})`}
          stroke="#f7cf52"
          strokeWidth="1"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5 text-[#f7cf52]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M12 2.25l2.91 5.897 6.512.947-4.71 4.595 1.111 6.48L12 17.77l-5.823 3.4 1.11-6.48-4.709-4.595 6.512-.947L12 2.25z" />
    </svg>
  );
}

function RatingStars({ rating }) {
  const safe = Number.isFinite(rating) ? rating : 0;
  const uid = useId();

  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const diff = safe - i + 1;
    const fill = diff >= 1 ? 1 : diff > 0 ? 0.5 : 0;
    stars.push(<Star key={i} filled={fill} gradientId={`${uid}-half-${i}`} />);
  }

  return <div className="flex items-center gap-1">{stars}</div>;
}

/* ----------------- helpers ----------------- */
function buildLocalISO(dateStr, timeStr) {
  const d = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

const absUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${API_BASE}${u.startsWith("/") ? "" : "/"}${u}`;
};

const COUNTRY_MAP = {
  UK: "GB",
  "United Kingdom": "GB",
  "U.K.": "GB",
  Ukraine: "UA",
  Italy: "IT",
  USA: "US",
  "United States": "US",
};

const normCountry = (c) => COUNTRY_MAP[c] || (c || "").toUpperCase();

function safeArray(v) {
  return Array.isArray(v) ? v.filter(Boolean) : [];
}

function formatMoneyUAH(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${Math.round(n).toLocaleString("uk-UA")} ₴`;
}

/* ── Availability Grid ───────────────────────────────────────────── */
const AVAIL_DAYS_UK = ["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

/**
 * Expands weekly recurring slots into concrete {dateStr, time} objects
 * for the next 14 days, skipping past times and blocked slots.
 */
function expandSlots(availability, blockedSlots) {
  if (!Array.isArray(availability) || availability.length === 0) return [];
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const blocked = new Set((blockedSlots || []).map((s) => `${s.date}|${s.time}`));
  const results = [];

  for (let d = 0; d < 14; d++) {
    const date = new Date(now);
    date.setDate(now.getDate() + d);
    const dayOfWeek = date.getDay();
    const dateStr = date.toISOString().slice(0, 10);

    for (const slot of availability) {
      if (slot.day !== dayOfWeek) continue;
      const startH = parseInt(slot.start, 10);
      const endH = parseInt(slot.end, 10);
      for (let h = startH; h < endH; h++) {
        const time = `${String(h).padStart(2, "0")}:00`;
        if (dateStr === todayStr) {
          const slotDate = new Date(`${dateStr}T${time}:00`);
          if (slotDate <= now) continue;
        }
        if (blocked.has(`${dateStr}|${time}`)) continue; // skip blocked
        results.push({ dateStr, time });
      }
    }
  }
  return results;
}

function AvailabilityGrid({ availability, blockedSlots, selectedDate, selectedTime, onPickSlot }) {
  const slots = expandSlots(availability, blockedSlots);
  if (slots.length === 0) return null;

  const byDate = {};
  for (const s of slots) {
    if (!byDate[s.dateStr]) byDate[s.dateStr] = [];
    byDate[s.dateStr].push(s.time);
  }
  const dates = Object.keys(byDate).sort().slice(0, 14);

  return (
    <div className="mt-4">
      <p className="mb-2 text-sm font-medium text-white/80">Доступний час</p>
      <div className="overflow-x-auto pb-1">
        <div className="flex gap-2 min-w-max">
          {dates.map((dateStr) => {
            const d = new Date(dateStr + "T12:00:00");
            const dayName = AVAIL_DAYS_UK[d.getDay()];
            const dayNum = d.getDate();
            const month = d.toLocaleDateString("uk-UA", { month: "short" }).replace(".", "");
            return (
              <div key={dateStr} className="flex flex-col gap-1 items-center">
                <div className="text-center mb-1">
                  <div className="text-[11px] font-semibold text-white/50 uppercase">{dayName}</div>
                  <div className="text-sm font-bold text-white">{dayNum}</div>
                  <div className="text-[10px] text-white/35">{month}</div>
                </div>
                {byDate[dateStr].map((time) => {
                  const isSelected = selectedDate === dateStr && selectedTime === time;
                  return (
                    <button
                      key={time}
                      type="button"
                      onClick={() => onPickSlot(dateStr, time)}
                      className={`w-16 rounded-xl px-1 py-1.5 text-xs font-medium transition-colors ${
                        isSelected
                          ? "bg-[#7269e3] text-white ring-1 ring-[#7269e3]"
                          : "bg-[#7269e3]/15 ring-1 ring-[#7269e3]/30 text-[#a09de8] hover:bg-[#7269e3]/30 hover:text-white"
                      }`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ----------------- Page ----------------- */
export default function TutorProfilePublic() {
  const { t, i18n } = useTranslation();
  const isUk = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase().startsWith("uk");
  const { id } = useParams();
  const navigate = useNavigate();

  // Auth: initialise synchronously from localStorage, then sync with Firebase
  // so we never show the booking form based on stale storage alone.
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(localStorage.getItem("auth") || "null"); }
    catch { return null; }
  });

  useEffect(() => {
    const unsub = fbAuth.onAuthStateChanged((fbUser) => {
      if (!fbUser) {
        // No Firebase session — clear any stale local storage and gate the form
        localStorage.removeItem("auth");
        localStorage.removeItem("isAuthed");
        setAuth(null);
        return;
      }
      // Email/password accounts must have verified their email
      const isEmailProvider = fbUser.providerData.some(
        (p) => p.providerId === "password"
      );
      if (isEmailProvider && !fbUser.emailVerified) {
        localStorage.removeItem("auth");
        localStorage.removeItem("isAuthed");
        setAuth(null);
        return;
      }
      // Firebase session is valid — read the stored profile
      try {
        setAuth(JSON.parse(localStorage.getItem("auth") || "null"));
      } catch {
        setAuth(null);
      }
    });
    return () => unsub();
  }, []);

  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reviewData, setReviewData] = useState({ reviews: [], thumbsUp: 0, thumbsDown: 0, total: 0 });

  const [form, setForm] = useState({
    date: "",
    time: "",
    note: "",
    format: "online",
    subject: "",
    level: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // When the specialist offers a free intro, the client picks what to book:
  // "intro" (15 min, free) or "full" (a normal paid consultation).
  const [bookingType, setBookingType] = useState("intro");

  // Fixed-price service packages of this specialist; ordering one switches the
  // booking panel into "package" mode.
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    let alive = true;
    apiFetch(`/api/packages/tutor/${encodeURIComponent(id)}`, { method: "GET" })
      .then((d) => { if (alive) setPackages(Array.isArray(d?.items) ? d.items : []); })
      .catch(() => { if (alive) setPackages([]); });
    return () => { alive = false; };
  }, [id]);

  const orderPackage = (p) => {
    setSelectedPackage(p);
    setSuccessMsg("");
    setSubmitError("");
    document.getElementById("booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const loadTutor = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const data = await apiFetch(`/api/tutors/${encodeURIComponent(id)}`, { method: "GET" });
      const raw = data?.tutor ?? data;
      const p = raw?.profile ?? {};

      const ratingValue =
        typeof p?.rating === "number"
          ? p.rating
          : typeof raw?.rating === "number"
            ? raw.rating
            : null;

      const mapped = {
        id: raw?.id,
        name: raw?.name || p?.displayName || t("profile.specialistFallback"),
        avatar: absUrl(p?.avatarUrl) || "",
        isVerified: !!raw?.isVerified,
        location: p?.city || "",
        subjects: safeArray(p?.subjects),
        levels: safeArray(p?.levels),
        languages: safeArray(p?.languages),
        rate: typeof p?.rate === "number" ? p.rate : null,
        currency: p?.currency || "UAH",
        trialIsFree: !!p?.trialIsFree,
        trialPrice: typeof p?.trialPrice === "number" ? p.trialPrice : null,
        rating: ratingValue,
        about: p?.bio || p?.about || "",
        availability: Array.isArray(p?.availability) ? p.availability : [],
        blockedSlots: Array.isArray(p?.blockedSlots) ? p.blockedSlots : [],
        degree: p?.degree || "",
        academicTitle: p?.academicTitle || "",
        experienceYears: typeof p?.experienceYears === "number" ? p.experienceYears : null,
        ...credentialFlags(p, raw?.isVerified),
      };

      setTutor(mapped);

      setForm((f) => ({
        ...f,
        subject: f.subject || mapped.subjects?.[0] || "",
        level: f.level || mapped.levels?.[0] || "",
      }));

      fetchTutorReviews(raw?.id || id).then(setReviewData).catch(() => {});
    } catch {
      setTutor(null);
      setLoadError(t("profile.errors.loadTutor"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!alive) return;
      await loadTutor();
    })();

    return () => {
      alive = false;
    };
  }, [loadTutor]);

  // Dynamic page meta — runs when tutor data loads
  useEffect(() => {
    if (!tutor) return;

    const subjectSnippet = tutor.subjects?.slice(0, 3).join(", ");
    const title = subjectSnippet
      ? `${tutor.name} — ${subjectSnippet} | Profio`
      : `${tutor.name} | Profio`;
    const description = tutor.about
      ? tutor.about.slice(0, 155).replace(/\n/g, " ")
      : `Book a session with ${tutor.name} on Profio.`;

    document.title = title;

    const setMeta = (property, content, useProperty = false) => {
      const attr = useProperty ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    if (tutor.avatar) setMeta("og:image", tutor.avatar, true);
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    return () => {
      document.title = "Profio — Find Specialists You Can Trust";
    };
  }, [tutor]);

  const submit = async (e) => {
    e.preventDefault();

    if (!auth) {
      navigate("/login");
      return;
    }

    if (!tutor?.id) return;

    setSubmitting(true);
    setSubmitError("");
    setSuccessMsg("");

    try {
      let preferredAt = null;

      const hasDate = !!form.date;
      const hasTime = !!form.time;

      if (hasDate !== hasTime) {
        setSubmitError(t("profile.errors.dateTimeTogether"));
        setSubmitting(false);
        return;
      }

      if (hasDate && hasTime) {
        const iso = buildLocalISO(form.date, form.time);

        if (!iso) {
          setSubmitError(t("profile.errors.invalidDateTime"));
          setSubmitting(false);
          return;
        }

        if (new Date(iso).getTime() <= Date.now()) {
          setSubmitError(t("profile.errors.futureDate"));
          setSubmitting(false);
          return;
        }

        preferredAt = iso;
      }

      const cleanNote = String(form.note || "").trim();
      if (cleanNote.length > 1000) {
        setSubmitError(t("profile.errors.noteTooLong"));
        setSubmitting(false);
        return;
      }

      // Free 15-min intro only when the specialist offers it AND the client
      // chose it; otherwise it's a full paid consultation at the hourly rate.
      const pkg = selectedPackage;
      const submitIsIntro = !pkg && !!tutor.trialIsFree && bookingType === "intro";

      await createTrialRequest({
        tutorId: tutor.id,
        tutorName: tutor.name,
        subject: form.subject || tutor.subjects?.[0] || undefined,
        level: form.level || null,
        mode: form.format,
        preferredAt,
        message: cleanNote,
        trialIsFree: submitIsIntro,
        trialPrice: submitIsIntro
          ? 0
          : typeof tutor.rate === "number"
            ? tutor.rate
            : null,
        // 15-min free intro ("знайомство") vs full 60-min paid consultation.
        durationMin: submitIsIntro ? 15 : 60,
        ...(pkg ? { packageId: pkg.id } : {}),
      });

      setSuccessMsg(t("profile.requestSent"));
      setForm((f) => ({ ...f, note: "", date: "", time: "" }));
    } catch {
      setSubmitError(t("profile.errors.sendRequest"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-6xl px-4 py-16">{t("common.loading")}</div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="rounded-2xl bg-white/5 p-10 ring-1 ring-white/10">
            <p className="text-red-200">{loadError}</p>

            <button
              onClick={() => navigate("/search")}
              className="mt-4 rounded-xl bg-[#7269e3] px-4 py-2 font-semibold hover:brightness-110"
            >
              {t("profile.backToSearch")}
            </button>

            <button
              onClick={loadTutor}
              className="ml-3 mt-4 rounded-xl border border-white/20 px-4 py-2 font-semibold hover:bg-white/5"
            >
              {t("common.retry")}
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (!tutor) {
    return (
      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="rounded-2xl bg-white/5 p-10 text-center ring-1 ring-white/10">
            <p className="text-white/80">{t("profile.notFound")}</p>
            <button
              onClick={() => navigate("/search")}
              className="mt-4 rounded-xl bg-[#7269e3] px-4 py-2 font-semibold hover:brightness-110"
            >
              {t("profile.backToSearch")}
            </button>
          </div>
        </div>
      </main>
    );
  }

  const displayName = tutor.name;
  const about = tutor.about;

  const [rawCity, ...rest] = (tutor.location || "").split(",").map((s) => s.trim());
  const rawCountry = rest.join(", ");

  const cityLabel = rawCity ? t(`cities.${rawCity}`, { defaultValue: rawCity }) : "";
  const countryLabel = rawCountry
    ? t(`countries.${normCountry(rawCountry)}`, { defaultValue: rawCountry })
    : "";
  const locationLabel = [cityLabel, countryLabel].filter(Boolean).join(", ");

  const subjectsLocalized = (tutor.subjects || []).map((s) =>
    t(`subjects.${toId(s)}`, { defaultValue: s })
  );
  const levelsLocalized = safeArray(tutor.levels).map((lvl) =>
    t(`levels.${toId(lvl)}`, { defaultValue: lvl })
  );

  const ratingNum = typeof tutor.rating === "number" ? tutor.rating : null;
  const hasRating = ratingNum !== null;

  const rateNum = Number(tutor.rate);
  const hasRate = Number.isFinite(rateNum);

  // Currency is the SPECIALIST'S own currency — not the viewer's language
  const isUAH = (tutor.currency || "UAH") === "UAH";
  // Per-hour label follows the viewer's language; currency symbol follows the specialist's
  const perHour = isUk ? "/год" : "/hr";
  const formatRate = (v) =>
    isUAH ? `${formatMoneyUAH(v)}${perHour}` : `€${v}${perHour}`;
  // Opt-in: specialist offers a free 15-min intro call ("знайомство").
  // When off, the booking panel shows a normal (paid) consultation instead.
  const offersFreeIntro = !!tutor.trialIsFree;
  // The client's effective choice: only specialists who offer the intro let the
  // client switch; otherwise it's always a full (paid) consultation.
  const effectiveType = selectedPackage ? "package" : offersFreeIntro ? bookingType : "full";
  const isIntro = effectiveType === "intro";
  const isPackage = effectiveType === "package";
  const pkgPrice = (p) => `${Number(p.price).toLocaleString("uk-UA")} ₴`;

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      {/* pb-44 on mobile clears BOTH stacked fixed bars (sticky booking CTA
          ~73px + bottom tab nav ~55px + safe area) so the booking form's
          submit button isn't hidden behind them. */}
      <div className="mx-auto max-w-6xl px-4 pt-10 pb-44 md:pb-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            {tutor.avatar && !tutor.avatar.endsWith("/") ? (
              <img
                crossOrigin="anonymous"
                src={tutor.avatar}
                alt={displayName}
                className="h-20 w-20 rounded-full object-cover ring-2 ring-white/20"
                loading="lazy"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="h-20 w-20 shrink-0 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center text-white/35">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10">
                  <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
                </svg>
              </div>
            )}

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-unbounded text-2xl font-extrabold leading-tight whitespace-normal break-words md:text-3xl">
                  {displayName}
                </h1>

                {tutor.fullyVerified ? (
                  <span
                    className="inline-flex items-center rounded-full bg-[#98dea3]/15 px-2.5 py-1 text-xs font-semibold text-[#98dea3] ring-1 ring-[#98dea3]/20"
                    title={t("credentials.badgeHint")}
                  >
                    ✓ {t("credentials.badge")}
                  </span>
                ) : tutor.isVerified ? (
                  <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/75 ring-1 ring-white/15">
                    ✓ {t("profile.verifiedTutor")}
                  </span>
                ) : null}
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-white/80">
                {locationLabel ? <span>{locationLabel}</span> : null}
                {locationLabel && subjectsLocalized.length ? <span>•</span> : null}
                {subjectsLocalized.length ? <span>{subjectsLocalized.join(", ")}</span> : null}
              </div>

              {(tutor.academicTitle || tutor.degree || typeof tutor.experienceYears === "number") && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  {tutor.academicTitle ? (
                    <span className="rounded-md bg-[#98dea3]/12 px-2 py-0.5 text-[#98dea3] ring-1 ring-[#98dea3]/20">
                      {t(`academic.title.${tutor.academicTitle}`, { defaultValue: tutor.academicTitle })}
                      {tutor.titleVerified ? <span title={t("credentials.confirmedMark")}> ✓</span> : null}
                    </span>
                  ) : null}
                  {tutor.degree ? (
                    <span className="rounded-md bg-white/10 px-2 py-0.5 text-white/75 ring-1 ring-white/10">
                      {t(`academic.degree.${tutor.degree}`, { defaultValue: tutor.degree })}
                      {tutor.degreeVerified ? <span className="text-[#98dea3]" title={t("credentials.confirmedMark")}> ✓</span> : null}
                    </span>
                  ) : null}
                  {typeof tutor.experienceYears === "number" ? (
                    <span className="text-white/55">{t("academic.expYears", { n: tutor.experienceYears })}</span>
                  ) : null}
                </div>
              )}

              <div className="mt-2 flex items-center gap-2">
                {hasRating ? (
                  <>
                    <RatingStars rating={ratingNum} />
                    <span className="text-xs text-white/70">{ratingNum.toFixed(1)}</span>
                  </>
                ) : (
                  <span className="rounded-md bg-white/10 px-2 py-0.5 text-xs text-white/75">
                    {t("search.newTutor")}
                  </span>
                )}

                <span className="ml-3 rounded-md bg-[#98dea3]/20 px-2 py-0.5 text-sm text-[#98dea3]">
                  {hasRate ? formatRate(rateNum) : t("profile.priceOnRequest")}
                </span>
              </div>
            </div>
          </div>

          {/* Book button — visible on desktop only; mobile uses sticky bottom CTA */}
          <a
            href="#booking"
            className="hidden self-start rounded-xl bg-[#7269e3] px-5 py-2 font-semibold hover:brightness-110 md:inline-flex md:self-center"
          >
            {isIntro ? t("profile.bookIntro") : t("profile.requestConsult")}
          </a>
        </div>

        {/* min-w-0 on both grid children: grid items default to
            min-width:auto, and the 14-day availability strip inside the
            booking aside (min-w-max ≈ 1000px) forced the whole grid wider
            than the phone screen — every section got cut off on the right. */}
        <div className="mt-8 grid gap-6 md:grid-cols-[minmax(0,1fr)_340px] lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 space-y-6">
            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <h2 className="font-unbounded text-xl font-semibold">{t("profile.about")}</h2>
              <p className="mt-3 leading-relaxed text-white/80">
                {about || t("profile.aboutPlaceholder")}
              </p>
            </section>

            {packages.length > 0 && (
              <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
                <h2 className="font-unbounded text-xl font-semibold">{t("packages.publicTitle")}</h2>
                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                  {packages.map((p) => (
                    <article
                      key={p.id}
                      className={`flex flex-col rounded-xl p-4 ring-1 transition ${
                        selectedPackage?.id === p.id ? "bg-[#7269e3]/10 ring-[#7269e3]/50" : "bg-white/[0.04] ring-white/10"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-semibold leading-snug">{p.title}</h3>
                        <span className="shrink-0 rounded-lg bg-white/10 px-2 py-0.5 text-sm font-semibold">{pkgPrice(p)}</span>
                      </div>
                      <div className="mt-1 text-xs text-white/55">{formatPackageMeta(t, p)}</div>
                      {p.audience ? <div className="mt-2 text-xs text-white/60">👥 {p.audience}</div> : null}
                      <ul className="mt-3 space-y-1 text-sm text-white/80">
                        {p.outcomes?.map((o, i) => (
                          <li key={i} className="flex gap-2"><span className="text-[#98dea3]">✓</span><span>{o}</span></li>
                        ))}
                      </ul>
                      {p.requirements ? (
                        <div className="mt-3 text-xs text-white/50">{t("packages.needFromYou")}: {p.requirements}</div>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => orderPackage(p)}
                        className="mt-4 self-start rounded-xl bg-[#7269e3] px-4 py-2 text-sm font-semibold hover:brightness-110"
                      >
                        {selectedPackage?.id === p.id ? `✓ ${t("packages.selected")}` : t("packages.order")}
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <h2 className="font-unbounded text-xl font-semibold">{t("profile.subjectsLevels")}</h2>

              <div className="mt-3 flex flex-wrap gap-2">
                {subjectsLocalized.length ? (
                  subjectsLocalized.map((s) => (
                    <span key={s} className="rounded-xl bg-white/10 px-3 py-1 text-sm">
                      {s}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-white/60">{t("search.noSubjects")}</span>
                )}
              </div>

              {!!levelsLocalized.length && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {levelsLocalized.map((lvl) => (
                    <span key={lvl} className="rounded-xl bg-white/10 px-3 py-1 text-sm">
                      {lvl}
                    </span>
                  ))}
                </div>
              )}

              {!!tutor.languages?.length && (
                <div className="mt-4">
                  <div className="mb-2 text-sm text-white/70">{t("profile.languages")}</div>
                  <div className="flex flex-wrap gap-2">
                    {tutor.languages.map((lng) => {
                      const key = toId(lng);
                      return (
                        <span key={lng} className="rounded-xl bg-white/10 px-3 py-1 text-sm">
                          {t(`languages.${key}`, { defaultValue: lng })}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-unbounded text-xl font-semibold">
                  {t("profile.reviews")}
                </h2>

                {/* Thumbs summary */}
                {reviewData.total > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-white/50">
                      {reviewData.total} {t("profile.consultations")}
                    </span>
                    <span className="flex items-center gap-1 rounded-lg bg-[#98dea3]/10 px-3 py-1 text-sm font-semibold text-[#98dea3]">
                      👍 {reviewData.thumbsUp}
                    </span>
                    {reviewData.thumbsDown > 0 && (
                      <span className="flex items-center gap-1 rounded-lg bg-red-500/10 px-3 py-1 text-sm font-semibold text-red-300">
                        👎 {reviewData.thumbsDown}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {reviewData.total === 0 ? (
                <div className="mt-3 rounded-xl bg-white/5 p-4 text-white/70 ring-1 ring-white/10">
                  {t("profile.reviewsEmpty")}
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {reviewData.reviews.map((r) => {
                    const name =
                      r.student?.profile?.displayName ||
                      r.student?.name ||
                      "Клієнт";
                    const date = new Date(r.createdAt).toLocaleDateString(
                      i18n.resolvedLanguage || "uk",
                      { month: "short", year: "numeric" }
                    );
                    const isPositive = r.isAuto || r.stars >= 4;

                    return (
                      <div key={r.id} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-base ${isPositive ? "text-[#98dea3]" : "text-red-300"}`}>
                              {isPositive ? "👍" : "👎"}
                            </span>
                            <span className="text-sm font-semibold text-white/90">{name}</span>
                            {r.isAuto && (
                              <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-white/35">
                                {t("profile.autoReview")}
                              </span>
                            )}
                          </div>
                          <span className="shrink-0 text-xs text-white/40">{date}</span>
                        </div>
                        {r.comment ? (
                          <p className="mt-2 text-sm leading-relaxed text-white/75">{r.comment}</p>
                        ) : r.isAuto ? (
                          <p className="mt-2 text-sm text-white/35 italic">
                            {t("profile.autoReviewText")}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <aside id="booking" className="min-w-0 h-max rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 md:sticky md:top-6">
            <div className="flex items-start justify-between gap-4">
              <h2 className="font-unbounded text-xl font-semibold">
                {t("profile.bookTitle")}
              </h2>

              {isPackage ? (
                <div className="text-right">
                  <span className="inline-flex items-center rounded-xl px-3 py-1.5 text-sm font-semibold bg-[#7269e3]/20 text-[#cfcaff] ring-1 ring-[#7269e3]/40">
                    {t("packages.fixedPrice")}
                  </span>
                  <div className="mt-1 text-base font-extrabold leading-tight text-white">{pkgPrice(selectedPackage)}</div>
                </div>
              ) : isIntro ? (
                <div className="text-right">
                  <span className="inline-flex items-center rounded-xl px-3 py-1.5 text-sm font-semibold bg-[#98dea3]/15 text-[#98dea3] ring-1 ring-[#98dea3]/35">
                    {t("profile.introBadge")}
                  </span>
                  <div className="mt-1 text-base font-extrabold leading-tight text-white">
                    {t("profile.trialFreeText")}
                  </div>
                </div>
              ) : (
                <div className="text-right">
                  <span className="inline-flex items-center rounded-xl px-3 py-1.5 text-sm font-semibold bg-[#7269e3]/20 text-[#cfcaff] ring-1 ring-[#7269e3]/40">
                    {t("profile.pricePerHour", { defaultValue: "Per hour" })}
                  </span>
                  <div className="mt-1 text-base font-extrabold leading-tight text-white">
                    {hasRate ? formatRate(rateNum) : t("profile.priceOnRequest")}
                  </div>
                </div>
              )}
            </div>

            {/* Client chooses: free 15-min intro OR a full paid consultation —
                only when the specialist offers the free intro. */}
            {isPackage && (
              <div className="mt-3 flex items-start justify-between gap-3 rounded-xl bg-[#7269e3]/10 px-3 py-2 ring-1 ring-[#7269e3]/30">
                <div className="min-w-0 text-sm">
                  <div className="text-xs text-white/55">{t("packages.selectedService")}</div>
                  <div className="font-semibold">{selectedPackage.title}</div>
                  <div className="text-xs text-white/55">{formatPackageMeta(t, selectedPackage)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPackage(null)}
                  className="shrink-0 text-xs text-white/60 hover:text-white"
                >
                  ✕ {t("packages.clear")}
                </button>
              </div>
            )}

            {offersFreeIntro && !isPackage && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setBookingType("intro")}
                  className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                    isIntro
                      ? "bg-[#98dea3]/20 text-[#98dea3] ring-1 ring-[#98dea3]/40"
                      : "bg-white/5 text-white/70 ring-1 ring-white/10 hover:bg-white/10"
                  }`}
                >
                  {t("profile.chooseIntro")}
                </button>
                <button
                  type="button"
                  onClick={() => setBookingType("full")}
                  className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                    !isIntro
                      ? "bg-[#7269e3]/25 text-[#cfcaff] ring-1 ring-[#7269e3]/45"
                      : "bg-white/5 text-white/70 ring-1 ring-white/10 hover:bg-white/10"
                  }`}
                >
                  {t("profile.chooseFull")}
                </button>
              </div>
            )}

            <p className="mt-3 text-sm text-white/60">
              {isPackage ? t("packages.bookingHint") : isIntro ? t("profile.intro.hint") : t("profile.consultHint")}
            </p>

            {!auth ? (
              /* ── Auth gate: shown instead of the form for guests ── */
              <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 px-5 py-6 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#7269e3]/20 text-2xl">
                  🔐
                </div>
                <p className="font-semibold text-white">
                  {t("profile.guestGate.title")}
                </p>
                <p className="mt-1 text-sm text-white/60">
                  {t("profile.guestGate.body")}
                </p>
                <Link
                  to={`/signup?next=${encodeURIComponent(window.location.pathname)}`}
                  className="mt-5 block w-full rounded-xl bg-[#7269e3] px-4 py-2.5 text-center font-semibold text-white shadow-[0_4px_14px_rgba(114,105,227,0.4)] hover:brightness-110"
                >
                  {t("profile.guestGate.signup")}
                </Link>
                <p className="mt-3 text-sm text-white/50">
                  {t("profile.guestGate.haveAccount")}{" "}
                  <Link
                    to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                    className="text-[#98dea3] hover:underline"
                  >
                    {t("profile.guestGate.login")}
                  </Link>
                </p>
              </div>
            ) : (

            /* ── Booking form: shown only for authenticated users ── */
            <form onSubmit={submit} className="mt-5 space-y-3">
              <div>
                <label className="mb-1 block text-sm text-white/80">{t("profile.subject")}</label>
                <select
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
                >
                  {(tutor.subjects?.length ? tutor.subjects : ["General"]).map((s) => (
                    <option key={s} value={s}>
                      {t(`subjects.${toId(s)}`, { defaultValue: s })}
                    </option>
                  ))}
                </select>
              </div>

              {tutor.levels?.length ? (
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("profile.level")}</label>
                  <select
                    value={form.level}
                    onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
                  >
                    <option value="">{t("profile.anyLevel")}</option>
                    {tutor.levels.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {t(`levels.${toId(lvl)}`, { defaultValue: lvl })}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* Format is always online — no picker needed */}

              {/* Availability grid — only way to pick date/time */}
              {tutor.availability?.length > 0 && (
                <AvailabilityGrid
                  availability={tutor.availability}
                  blockedSlots={tutor.blockedSlots}
                  selectedDate={form.date}
                  selectedTime={form.time}
                  onPickSlot={(dateStr, time) => {
                    // clicking the same slot deselects it
                    if (form.date === dateStr && form.time === time) {
                      setForm((f) => ({ ...f, date: "", time: "" }));
                    } else {
                      setForm((f) => ({ ...f, date: dateStr, time }));
                    }
                    setSubmitError("");
                  }}
                />
              )}

              <div>
                <label className="mb-1 block text-sm text-white/80">{t("profile.messageOptional")}</label>
                <textarea
                  rows={3}
                  maxLength={1000}
                  placeholder={t("profile.messagePlaceholder")}
                  value={form.note}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, note: e.target.value }));
                    setSubmitError("");
                  }}
                  className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 w-full rounded-xl bg-[#7269e3] px-4 py-2 font-semibold hover:brightness-110 disabled:opacity-60"
              >
                {submitting
                  ? t("profile.sending")
                  : isPackage
                    ? t("packages.orderSubmit")
                    : isIntro
                      ? t("profile.bookIntro")
                      : t("profile.requestConsult")}
              </button>

              {submitError ? (
                <div className="mt-2 rounded-xl border border-red-300/20 bg-red-500/10 p-3 text-sm text-red-100">
                  {submitError}
                </div>
              ) : null}

              {successMsg ? (
                <div className="mt-2 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white/90">
                  {successMsg}
                </div>
              ) : null}

              <p className="mt-2 text-xs text-white/60">{t("profile.noPayment")}</p>
            </form>
            )}
          </aside>
        </div>
      </div>

      {/* ── Mobile sticky booking bar ─────────────────────────────── */}
      {/* Sits ABOVE the app's bottom tab bar (MobileBottomNav, ~64px tall,
          z-40) — with bottom-0 it rendered underneath the nav and peeked out
          as a clipped purple pill. */}
      <div
        className="fixed left-0 right-0 z-30 md:hidden border-t border-white/10 bg-[#272c39]/96 backdrop-blur-md px-4 py-3"
        // Sit directly above the bottom tab bar. The nav is ~55px tall PLUS the
        // iOS safe-area inset (home indicator), so a hardcoded 64px overlapped
        // the nav on notched iPhones. calc() tracks the real inset.
        style={{ bottom: "calc(55px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            <div className="text-[11px] text-white/50">
              {isPackage ? selectedPackage.title : isIntro ? t("profile.introBadge") : t("profile.pricePerHour", { defaultValue: "Per hour" })}
            </div>
            <div className="font-bold leading-tight text-white">
              {isPackage
                ? pkgPrice(selectedPackage)
                : isIntro
                ? t("profile.trialFreeText")
                : hasRate
                  ? formatRate(rateNum)
                  : t("profile.priceOnRequest")}
            </div>
          </div>
          {auth ? (
            <a
              href="#booking"
              className="flex-1 rounded-xl bg-[#7269e3] py-3 text-center font-semibold text-white hover:brightness-110 active:scale-[0.98] transition"
            >
              {isIntro ? t("profile.bookIntro") : t("profile.requestConsult")}
            </a>
          ) : (
            <Link
              to={`/signup?next=${encodeURIComponent(window.location.pathname)}`}
              className="flex-1 rounded-xl bg-[#7269e3] py-3 text-center font-semibold text-white hover:brightness-110 active:scale-[0.98] transition"
            >
              {t("profile.guestGate.signup")}
            </Link>
          )}
        </div>
      </div>
    </main>
  );
}